import { type AxiosError } from 'axios'
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { type CachedHierarchyEntryResponseDto } from '@redteclab/api/clients/bully'

import { useOpenableControls } from '../../../../../../generic-hooks'
import { cmsStoryblokSlugToPublicUrl } from '../../../../../cmsStoryblokSlugToPublicUrl'

import { useCategoryHierarchyNavigationSwr } from './swr/useCategoryHierarchyNavigationSwr'

type CategoriesNavigationContext = {
  animationDirection?: 'backward' | 'forward' | null
  currentCategoryData: CachedHierarchyEntryResponseDto | null | undefined
  error: AxiosError | undefined
  handleClose: () => void
  handleOpen: () => void
  isLoading: boolean
  isOpen: boolean
  navigateToCategory: (
    storyblokCategorySlug: string,
    animateDirection: 'backward' | 'forward' | null,
  ) => void
  previouseCategoryData: CachedHierarchyEntryResponseDto | null | undefined
}

const CategoriesNavigationContext = createContext<CategoriesNavigationContext>({
  animationDirection: null,
  currentCategoryData: null,
  error: undefined,
  // eslint-disable-next-line @typescript-eslint/strict-void-return -- disabled during ESLint config update, refactor if you change this code
  handleClose: () => null,
  // eslint-disable-next-line @typescript-eslint/strict-void-return -- disabled during ESLint config update, refactor if you change this code
  handleOpen: () => null,
  isLoading: false,
  isOpen: false,
  // eslint-disable-next-line @typescript-eslint/strict-void-return -- disabled during ESLint config update, refactor if you change this code
  navigateToCategory: () => null,
  previouseCategoryData: null,
})

const ANIMATION_DURATION_MS = 300

export const StoryblokCategoryNavigationProvider: FC<
  PropsWithChildren<{
    initialCategoryData: CachedHierarchyEntryResponseDto
  }>
> = ({ children, initialCategoryData }) => {
  const initialCategoryDataToShow =
    !initialCategoryData.hasChildren && initialCategoryData.parent
      ? initialCategoryData.parent
      : initialCategoryData

  const { handleClose, handleOpen, isOpen } = useOpenableControls()
  const [storyblokCategorySlug, setStoryblokCategorySlug] = useState<string>(
    () => initialCategoryDataToShow.fullSlug,
  )

  const [previousCategoryData, setPreviousCategoryData] =
    useState<CachedHierarchyEntryResponseDto | null>(null)
  const [animationDirection, setAnimationDirection] = useState<
    'backward' | 'forward' | null
  >(null)

  const {
    data: currentCategoryData,
    error,
    isLoading,
  } = useCategoryHierarchyNavigationSwr({
    initialData: initialCategoryDataToShow,
    shouldFetchData: isOpen,
    storyblokCategorySlug,
  })

  const navigateToCategory = useCallback(
    (
      storySlug: string,
      animateDirection: 'backward' | 'forward' | null,
    ): void => {
      setPreviousCategoryData(
        storyblokCategorySlug === initialCategoryDataToShow.fullSlug
          ? initialCategoryDataToShow
          : (currentCategoryData ?? null),
      )
      setStoryblokCategorySlug(storySlug)
      setAnimationDirection(animateDirection)
    },
    [storyblokCategorySlug, currentCategoryData, initialCategoryDataToShow],
  )

  useEffect(() => {
    if (error) {
      // in case of error we want to redirect user to the category page,
      // as the navigation is a nice to have and not critical,
      // and we don't want to block users because of it
      location.assign(cmsStoryblokSlugToPublicUrl(storyblokCategorySlug))
    }
  }, [error, storyblokCategorySlug])

  useEffect(() => {
    if (currentCategoryData && previousCategoryData) {
      const timeoutId = setTimeout(() => {
        setPreviousCategoryData(null)
        setAnimationDirection(null)
      }, ANIMATION_DURATION_MS)

      return (): void => {
        clearTimeout(timeoutId)
      }
    }

    return undefined
  }, [currentCategoryData, previousCategoryData, storyblokCategorySlug])

  const values = useMemo(
    () => ({
      animationDirection,
      currentCategoryData: storyblokCategorySlug
        ? currentCategoryData
        : initialCategoryDataToShow,
      error,
      handleClose,
      handleOpen,
      isLoading,
      isOpen,
      navigateToCategory,
      previouseCategoryData: previousCategoryData,
    }),
    [
      animationDirection,
      storyblokCategorySlug,
      currentCategoryData,
      initialCategoryDataToShow,
      error,
      handleClose,
      handleOpen,
      isLoading,
      isOpen,
      navigateToCategory,
      previousCategoryData,
    ],
  )

  return (
    <CategoriesNavigationContext.Provider value={values}>
      {children}
    </CategoriesNavigationContext.Provider>
  )
}

export const useCategoriesNavigationContext = (): CategoriesNavigationContext =>
  useContext(CategoriesNavigationContext)
